import { Suspense, useState } from 'react';
import { Provider } from 'react-redux';
import { seedFeatureFlags, setupStore, type AppStore } from './state/Store.ts';
import { BrowserRouter, StaticRouter } from 'react-router';
import { ssrFlags } from '@/ssr/ssrData';
import { AppRoutes } from './components/AppRoutes.tsx';
import { ScrollToTop } from './components/ScrollToTop';
import { LocaleCatalog } from '@/components/LocaleCatalog';
import { CampaignShortLinkRedirect } from '@/components/CampaignShortLinkRedirect';
import { MarketingContextCapture } from '@/components/MarketingContextCapture';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Toaster } from '@/components/ui/sonner';
import { ReleaseSwitcher } from '@/components/ReleaseSwitcher/ReleaseSwitcher';
import { LoadThirdPartyConfigs } from './LoadThirdParty.tsx';
import { AppProvider } from '@/contexts/AppContext/AppProviderContext.tsx';
import { ThemeProvider } from '@/contexts/ThemeContext/ThemeProvider';
import { LayoutProvider } from '@/contexts/LayoutContext/LayoutProvider';
import { OfflineDetector } from '@/components/OfflineDetector/OfflineDetector';
import GoZigLogo from '@/assets/logos/goZig-logo-standalone.svg';
import { SheetVisibilityProvider } from './contexts/SheetVisibilityContext/SheetVisibilityProvider.tsx';
import { ChatMachineProvider } from './machines/ChatMachineContext.tsx';
import { TiltPermissionProvider } from '@/components/SkillFitDonut/TiltPermissionProvider';
import { SKILL_FIT_TRACK_LOCATION } from '@/pages/Career/Qualifications/SkillFit/skillFitTracking';

/** Minimal fallback with no theme/config deps so something always shows while lazy chunks load. */
const SuspenseFallback = () => (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-white p-6">
        <img
            src={GoZigLogo}
            alt="GoZig Logo"
            className="h-13 lg:h-16 w-auto mb-4"
        />
        <span className="text-gray-600 text-lg">Loading…</span>
    </div>
);

export interface AppProps {
    /**
     * Present only during a server render. `location` drives `StaticRouter`, and `store` is the
     * store the render already primed, so the markup and the serialized state cannot disagree.
     */
    ssr?: {
        location: string;
        store: AppStore;
    };
}

export const App = ({ ssr }: AppProps = {}) => {
    // One store per mount. On a hydrating client it starts with the feature flags the server
    // rendered with, so the first render draws the same thing the server did.
    const [store] = useState<AppStore>(() => {
        if (ssr) return ssr.store;
        const clientStore = setupStore();
        const flags = ssrFlags();
        if (flags) seedFeatureFlags(clientStore, flags);
        return clientStore;
    });
    setupListeners(store.dispatch);

    const tree = (
        <Provider store={store}>
            <ThemeProvider>
                <AppProvider>
                    <LoadThirdPartyConfigs>
                        <LayoutProvider>
                            <OfflineDetector>
                                <SheetVisibilityProvider>
                                    <ChatMachineProvider>
                                        <ScrollToTop />
                                        <LocaleCatalog />
                                        <CampaignShortLinkRedirect />
                                        <MarketingContextCapture />
                                        {/* Outside Suspense so a loading route chunk
                                            cannot tear down the answer it holds. */}
                                        <TiltPermissionProvider trackLocation={SKILL_FIT_TRACK_LOCATION}>
                                            <Suspense fallback={<SuspenseFallback />}>
                                                <AppRoutes />
                                            </Suspense>
                                        </TiltPermissionProvider>
                                        <Toaster />
                                        <ReleaseSwitcher />
                                    </ChatMachineProvider>
                                </SheetVisibilityProvider>
                            </OfflineDetector>
                        </LayoutProvider>
                    </LoadThirdPartyConfigs>
                </AppProvider>
            </ThemeProvider>
        </Provider>
    );

    // Two routers, one tree. Written as a conditional rather than a computed component so the
    // element type is stable: a component created during render would remount everything below
    // it on every re-render of App.
    return ssr
        ? <StaticRouter location={ssr.location}>{tree}</StaticRouter>
        : <BrowserRouter>{tree}</BrowserRouter>;
};
